import React, { useState, useRef, useEffect } from "react";
import {
  Syringe,
  Stethoscope,
  Pill,
  Plus,
  X,
  Edit3,
  Save,
  Camera,
  Heart,
  Trash2,
  Info,
  Repeat,
  BellRing,
  Check,
  QrCode,
  Trophy,
  Settings,
  ZoomIn,
} from "lucide-react";
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  getDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { Reminder, DOG_BREEDS, calcAge, formatDate } from "../utils/helpers";
import { useAppContext } from "../context/AppContext";
import Cropper from "react-easy-crop";
import getCroppedImg from "../utils/cropImage";

// 🚀 Виправляємо помилку TS2604 (змушуємо TS розуміти цей компонент)
const EasyCropper = Cropper as any;

import MedicalScanner from "../components/MedicalScanner";
import ProfileSettings from "../components/ProfileSettings";
import QrOrderModal from "../components/QrOrderModal";
import MedicalInfoModal from "../components/MedicalInfoModal";
import ReminderForm from "../components/ReminderForm";

interface ProfileProps {
  reminders: Reminder[];
  points: number;
  setPoints: (p: number) => void;
}

const getLevelInfo = (pts: number, lang: string) => {
  if (pts < 100)
    return {
      level: 1,
      current: pts,
      max: 100,
      percent: (pts / 100) * 100,
      title: lang === "uk" ? "Новачок" : "Beginner",
    };
  if (pts < 250)
    return {
      level: 2,
      current: pts - 100,
      max: 150,
      percent: ((pts - 100) / 150) * 100,
      title: lang === "uk" ? "Дбайливий друг" : "Caring Friend",
    };
  if (pts < 500)
    return {
      level: 3,
      current: pts - 250,
      max: 250,
      percent: ((pts - 250) / 250) * 100,
      title: lang === "uk" ? "Досвідчений опікун" : "Pro Guardian",
    };
  if (pts < 1000)
    return {
      level: 4,
      current: pts - 500,
      max: 500,
      percent: ((pts - 500) / 500) * 100,
      title: lang === "uk" ? "Експерт" : "Expert",
    };
  return {
    level: 5,
    current: pts - 1000,
    max: 1000,
    percent: Math.min(((pts - 1000) / 1000) * 100, 100),
    title: lang === "uk" ? "Легенда" : "Legend",
  };
};

export default function Profile({
  reminders,
  points,
  setPoints,
}: ProfileProps) {
  const { lang, t, user, pets, activePetId, setActivePetId, activePet } =
    useAppContext();
  const lvlInfo = getLevelInfo(points, lang || "uk");

  const fileRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [showBreedSuggestions, setShowBreedSuggestions] = useState(false);
  const [rewardToast, setRewardToast] = useState({ show: false, text: "" });

  const [showForm, setShowForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);

  const [ownerName, setOwnerName] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);

  // СТЕЙТИ ДЛЯ ОБРІЗАННЯ ФОТО
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, "users", user.uid)).then((d) => {
      if (d.exists()) {
        setOwnerName(d.data().ownerName || "");
        setOwnerPhone(d.data().ownerPhone || "");
      }
    });
    const unsubFollowing = onSnapshot(doc(db, "users", user.uid), (d) =>
      setFollowingCount(d.data()?.following?.length || 0)
    );
    const unsubFollowers = onSnapshot(
      query(
        collection(db, "users"),
        where("following", "array-contains", user.uid)
      ),
      (snap) => setFollowersCount(snap.size)
    );
    return () => {
      unsubFollowing();
      unsubFollowers();
    };
  }, [user]);

  // 🚀 Оновлений вибір файлу: використовуємо надлегкий URL.createObjectURL спеціально для iOS Safari
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);

    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  const handleSaveCroppedImage = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;
    try {
      const croppedResult = await getCroppedImg(imageToCrop, croppedAreaPixels);
      setDraft({ ...draft, photo: croppedResult });
      setImageToCrop(null);
    } catch (err) {
      console.error(err);
      alert("Помилка обрізання фото");
    }
  };

  const saveProfile = async () => {
    if (!user || !draft) return;
    const saveId = draft.id || Date.now().toString();
    await setDoc(doc(db, "users", user.uid, "pets", saveId.toString()), {
      ...draft,
      id: saveId,
      userId: user.uid,
    });
    if (draft.city)
      await setDoc(
        doc(db, "users", user.uid),
        { city: draft.city },
        { merge: true }
      );
    setActivePetId(saveId);
    setEditing(false);
  };

  const addNewPet = async () => {
    if (!user) return;
    const newId = Date.now().toString();
    const newPet = {
      id: newId,
      userId: user.uid,
      name: t.newPet,
      species: "Dog",
      breed: "",
      gender: "",
      dob: "",
      chip: "",
      weight: "",
      features: "",
      photo: null,
      city: "",
      diagnoses: [],
      meds: [],
      allergies: [],
      isSterilized: false,
      isHeatActive: false,
      heatStartDate: null,
      heatHistory: [],
    };
    await setDoc(doc(db, "users", user.uid, "pets", newId), newPet);
    setActivePetId(newId);
    setDraft(newPet);
    setEditing(true);
  };

  const handleDeleteReminder = async (id: number | string) => {
    if (user)
      await deleteDoc(
        doc(db, "users", user.uid, "reminders", String(id))
      ).catch(() => {});
  };

  const handleCompleteReminder = async (r: Reminder) => {
    if (user && !r.completed) {
      const earned = r.type === t.modVac ? 50 : 10;
      await updateDoc(doc(db, "users", user.uid), { points: points + earned });
      setRewardToast({
        show: true,
        text: `+${earned} ${lang === "uk" ? "балів!" : "pts!"}`,
      });
      setTimeout(() => setRewardToast({ show: false, text: "" }), 3000);

      if (r.recurrence === "none") {
        await deleteDoc(doc(db, "users", user.uid, "reminders", String(r.id)));
      } else {
        const d = new Date(r.date + "T00:00:00");
        if (r.recurrence === "monthly") d.setMonth(d.getMonth() + 1);
        else if (r.recurrence === "6months") d.setMonth(d.getMonth() + 6);
        else if (r.recurrence === "yearly") d.setFullYear(d.getFullYear() + 1);
        else if (r.recurrence.startsWith("custom_"))
          d.setDate(
            d.getDate() + parseInt(r.recurrence.replace("custom_", ""))
          );
        await setDoc(doc(db, "users", user.uid, "reminders", String(r.id)), {
          ...r,
          completed: true,
          nextDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(d.getDate()).padStart(2, "0")}`,
        });
      }
    }
  };

  return (
    <div className="animate-fade-in relative">
      {rewardToast.show && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[999] pointer-events-none animate-bounce flex flex-col items-center">
          <div className="text-5xl mb-2">🎉</div>
          <div className="bg-[#10b981] text-white font-black text-2xl px-6 py-3 rounded-2xl shadow-2xl border-4 border-white">
            {rewardToast.text}
          </div>
        </div>
      )}

      {/* МОДАЛКА ОБРІЗАННЯ ФОТО */}
      {imageToCrop && (
        <div className="fixed inset-0 bg-black/90 z-[999] flex flex-col justify-between p-6 backdrop-blur-md animate-fade-in">
          <div className="flex justify-between items-center text-white shrink-0 mt-8">
            <h3 className="font-extrabold text-lg">
              {lang === "uk" ? "Редагування photo" : "Crop Photo"}
            </h3>
            <button
              onClick={() => setImageToCrop(null)}
              className="p-2 bg-white/10 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          <div className="relative flex-1 my-6 rounded-2xl overflow-hidden bg-black/40 border border-white/10">
            <EasyCropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(croppedArea: any, croppedAreaPixels: any) =>
                setCroppedAreaPixels(croppedAreaPixels)
              }
            />
          </div>

          <div className="flex flex-col gap-5 shrink-0 mb-4">
            <div className="flex items-center gap-3 bg-white/10 px-4 py-3 rounded-xl border border-white/10">
              <ZoomIn size={18} className="text-white/60" />
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full h-1 bg-white/20 rounded-lg appearance-none accent-[#a073e7]"
              />
            </div>
            <button
              onClick={handleSaveCroppedImage}
              className="w-full py-4 bg-[#10b981] text-white font-bold rounded-xl shadow-md active:scale-95 transition-all text-sm"
            >
              {lang === "uk" ? "Застосувати" : "Apply Crop"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-[#a073e7] to-[#b991f8] px-5 pb-14 pt-12 relative overflow-hidden rounded-b-[2.5rem]">
        <img
          src="/paw-white.png"
          className="absolute -top-4 -right-4 w-40 opacity-15 rotate-12"
        />

        <div className="flex justify-between items-start text-white mb-6 relative z-10">
          <div>
            <h1 className="font-extrabold text-2xl">MyPaws</h1>
            <p className="opacity-80 text-xs tracking-widest font-bold uppercase">
              {t.welcome}, {user?.displayName?.split(" ")[0] || ownerName}!
            </p>
          </div>
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 bg-white/20 rounded-full text-white border border-white/30 active:scale-95 transition-all shadow-sm"
          >
            <Settings size={20} />
          </button>
        </div>

        <div className="bg-white/10 border border-white/20 rounded-2xl p-4 relative z-10 backdrop-blur-sm mb-6 shadow-inner text-white">
          <div className="flex justify-between items-end mb-2">
            <div className="flex items-center gap-2">
              <div className="bg-white text-[#a073e7] w-8 h-8 rounded-full flex items-center justify-center font-black shadow-md">
                <Trophy size={16} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-white/80">
                  {lang === "uk" ? "Рівень" : "Level"} {lvlInfo.level}
                </p>
                <p className="font-extrabold text-sm text-white">
                  {lvlInfo.title}
                </p>
              </div>
            </div>
            <div className="text-xs font-bold text-white text-right">
              {lvlInfo.current}{" "}
              <span className="text-white/60">
                / {lvlInfo.max} {t.pts}
              </span>
            </div>
          </div>
          <div className="w-full bg-black/20 h-2.5 rounded-full overflow-hidden shadow-inner">
            <div
              className="bg-[#10b981] h-full rounded-full transition-all duration-1000 ease-out relative"
              style={{ width: `${lvlInfo.percent}%` }}
            >
              <div className="absolute inset-0 bg-white/20 w-full h-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto no-scrollbar relative z-10">
          {pets.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                setActivePetId(p.id);
                setEditing(false);
              }}
              className={`shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
                activePetId === p.id
                  ? "bg-white text-[#a073e7] shadow-md font-extrabold"
                  : "bg-white/20 text-white font-bold"
              }`}
            >
              <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-[10px] overflow-hidden">
                {p.photo ? (
                  <img src={p.photo} className="w-full h-full object-cover" />
                ) : (
                  p.name?.[0]
                )}
              </div>
              <span className="text-sm pr-1">{p.name}</span>
            </button>
          ))}
          <button
            onClick={addNewPet}
            className="shrink-0 w-9 h-9 rounded-full bg-[#fff] bg-opacity-20 flex items-center justify-center text-white border border-white/30"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      <div className="px-4 -mt-8 relative z-10">
        <div className="bg-[var(--bg-card)] rounded-3xl p-5 shadow-lg mb-6">
          <div className="flex gap-4 items-start mb-5">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-[var(--accent-soft)] border-4 border-[var(--bg-card)] shadow-md flex items-center justify-center text-3xl font-bold text-[#a073e7] overflow-hidden">
                {(editing ? draft?.photo : activePet?.photo) ? (
                  <img
                    src={editing ? draft?.photo : activePet?.photo}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  activePet?.name?.[0]
                )}
              </div>
              {editing && (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-[#ff7a87] rounded-full border-2 border-[var(--bg-card)] flex items-center justify-center shadow-md text-white"
                >
                  <Camera size={14} />
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>

            <div className="flex-1 pt-1 min-w-0 relative flex flex-col gap-1.5">
              <div className="flex justify-between items-center w-full">
                {editing ? (
                  <input
                    className="w-full p-2 bg-[var(--bg-soft)] text-[var(--text-main)] border-[var(--border-color)] border rounded-xl font-bold outline-none"
                    value={draft?.name}
                    onChange={(e) =>
                      setDraft({ ...draft, name: e.target.value })
                    }
                    placeholder={t.name}
                  />
                ) : (
                  <h2 className="text-2xl font-extrabold truncate text-[var(--text-main)]">
                    {activePet.name}
                  </h2>
                )}

                <div className="flex gap-2 shrink-0 ml-2">
                  {!editing ? (
                    <>
                      <button
                        onClick={() => setShowQrModal(true)}
                        className="p-2 bg-[var(--bg-soft)] text-[#a073e7] rounded-full active:scale-95 shadow-sm border border-[var(--border-color)] transition-all"
                      >
                        <QrCode size={18} />
                      </button>
                      <button
                        onClick={() => setShowInfo(true)}
                        className="p-2 bg-[var(--bg-soft)] text-[#a073e7] rounded-full active:scale-95 shadow-sm border border-[var(--border-color)] transition-all"
                      >
                        <Info size={18} />
                      </button>
                      <button
                        onClick={() => {
                          setDraft(activePet);
                          setEditing(true);
                        }}
                        className="p-2 bg-[var(--bg-soft)] text-[#a073e7] rounded-full active:scale-95 shadow-sm border border-[var(--border-color)] transition-all"
                      >
                        <Edit3 size={18} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setEditing(false)}
                      className="p-2 bg-[var(--red-soft)] text-[var(--red-text)] rounded-full active:scale-95 shadow-sm border border-[var(--red-soft)] transition-all"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>

              {editing ? (
                <div className="relative">
                  <input
                    className="w-full p-2 bg-[var(--bg-soft)] text-[var(--text-main)] border-[var(--border-color)] border rounded-xl outline-none"
                    value={draft?.breed || ""}
                    onChange={(e) => {
                      setDraft({ ...draft, breed: e.target.value });
                      setShowBreedSuggestions(true);
                    }}
                    onFocus={() => setShowBreedSuggestions(true)}
                    onBlur={() =>
                      setTimeout(() => setShowBreedSuggestions(false), 200)
                    }
                    placeholder={t.breed}
                  />
                  {showBreedSuggestions && (
                    <div className="absolute z-50 w-full bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl mt-1 shadow-2xl max-h-[150px] overflow-y-auto no-scrollbar">
                      {DOG_BREEDS.filter((b) =>
                        b
                          .toLowerCase()
                          .includes((draft?.breed || "").toLowerCase())
                      ).map((b) => (
                        <div
                          key={b}
                          className="px-4 py-2 text-sm text-[var(--text-main)] border-b border-[var(--border-color)] last:border-0 active:bg-[var(--bg-soft)]"
                          onClick={() => {
                            setDraft({ ...draft, breed: b });
                            setShowBreedSuggestions(false);
                          }}
                        >
                          {b}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[var(--text-muted)] font-semibold text-sm truncate leading-none">
                  {activePet.breed || t.notSpec}
                </p>
              )}

              {!editing && (
                <div className="flex flex-wrap items-center gap-2">
                  {activePet.dob && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--accent-soft)] text-[#a073e7] text-xs font-bold rounded-full">
                      <Heart size={12} fill="currentColor" />{" "}
                      {calcAge(activePet.dob, t)}
                    </div>
                  )}
                  {(activePet as any).isHeatActive && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#ef4444] text-white text-xs font-bold rounded-full shadow-sm animate-pulse">
                      <Heart size={12} fill="currentColor" />{" "}
                      {lang === "uk" ? "Тічка зараз" : "In Heat"}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {!editing && (
            <div className="flex gap-4 mb-5 pt-4 border-t border-[var(--border-color)]">
              <div className="flex flex-col items-center flex-1">
                <span className="font-black text-xl text-[var(--text-main)]">
                  {followersCount}
                </span>
                <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider">
                  {lang === "uk" ? "Підписники" : "Followers"}
                </span>
              </div>
              <div className="w-px bg-[var(--border-color)]"></div>
              <div className="flex flex-col items-center flex-1">
                <span className="font-black text-xl text-[var(--text-main)]">
                  {followingCount}
                </span>
                <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider">
                  {lang === "uk" ? "Підписки" : "Following"}
                </span>
              </div>
            </div>
          )}

          {editing && (
            <div className="mb-4 relative">
              <div className="flex justify-between items-end mb-1">
                <p className="text-[10px] uppercase font-bold text-[var(--text-muted)]">
                  {t.medInfo}
                </p>
                <MedicalScanner draft={draft} setDraft={setDraft} />
              </div>
              <textarea
                className="w-full p-3 bg-[var(--bg-soft)] border border-[var(--border-accent)] text-[var(--text-main)] rounded-xl text-base outline-none min-h-[100px]"
                value={draft?.features || ""}
                onChange={(e) =>
                  setDraft({ ...draft, features: e.target.value })
                }
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                {t.gender}
              </p>
              {editing ? (
                <select
                  className="w-full p-2 bg-[var(--bg-soft)] border-[var(--border-color)] border rounded-xl outline-none text-[var(--text-main)]"
                  value={draft?.gender || ""}
                  onChange={(e) =>
                    setDraft({ ...draft, gender: e.target.value })
                  }
                >
                  <option value="">{t.notSpec}</option>
                  <option value={t.boy}>{t.boy}</option>
                  <option value={t.girl}>{t.girl}</option>
                </select>
              ) : (
                <p className="font-medium text-[15px] text-[var(--text-main)]">
                  {activePet.gender || "—"}
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                {t.dob}
              </p>
              {editing ? (
                <input
                  type="date"
                  className="w-full p-2 bg-[var(--bg-soft)] text-[var(--text-main)] border-[var(--border-color)] border rounded-xl outline-none"
                  value={draft?.dob || ""}
                  onChange={(e) => setDraft({ ...draft, dob: e.target.value })}
                />
              ) : (
                <p className="font-medium text-[15px] text-[var(--text-main)]">
                  {formatDate(activePet.dob)}
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                {t.weight}
              </p>
              {editing ? (
                <input
                  className="w-full p-2 bg-[var(--bg-soft)] text-[var(--text-main)] border-[var(--border-color)] border rounded-xl outline-none"
                  value={draft?.weight || ""}
                  onChange={(e) =>
                    setDraft({ ...draft, weight: e.target.value })
                  }
                  placeholder="kg"
                />
              ) : (
                <p className="font-medium text-[15px] text-[var(--text-main)]">
                  {activePet.weight ? `${activePet.weight}` : "—"}
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                {t.city}
              </p>
              {editing ? (
                <input
                  className="w-full p-2 bg-[var(--bg-soft)] text-[var(--text-main)] border-[var(--border-color)] border rounded-xl outline-none"
                  value={draft?.city || ""}
                  onChange={(e) => setDraft({ ...draft, city: e.target.value })}
                />
              ) : (
                <p className="font-medium text-[15px] text-[var(--text-main)]">
                  {activePet.city || "—"}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1">
                {t.chip}
              </p>
              {editing ? (
                <input
                  className="w-full p-2 bg-[var(--bg-soft)] text-[var(--text-main)] border-[var(--border-color)] border rounded-xl outline-none"
                  value={draft?.chip || ""}
                  onChange={(e) => setDraft({ ...draft, chip: e.target.value })}
                />
              ) : (
                <p className="font-medium text-[15px] text-[var(--text-main)]">
                  {activePet.chip || "—"}
                </p>
              )}
            </div>

            {editing &&
              (draft?.gender === t.girl ||
                draft?.gender === "Дівчинка" ||
                draft?.gender === "Girl") && (
                <div className="col-span-2 flex items-center justify-between bg-[var(--red-soft)] border border-[#ef4444] border-opacity-30 px-4 py-3 rounded-xl mt-1">
                  <span className="text-sm font-black text-[#ef4444] flex items-center gap-2">
                    <Heart size={16} />{" "}
                    {lang === "uk" ? "Стерилізована?" : "Sterilized?"}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        isSterilized: !draft.isSterilized,
                        isHeatActive: false,
                      })
                    }
                    className={`w-12 h-7 rounded-full flex items-center p-1 transition-all shadow-inner ${
                      draft?.isSterilized
                        ? "bg-[#10b981]"
                        : "bg-[var(--bg-card)] border border-[var(--border-color)]"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                        draft?.isSterilized ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              )}
          </div>

          {editing && (
            <div className="mt-5 flex flex-col gap-3">
              <button
                onClick={saveProfile}
                className="w-full py-3 bg-[#10b981] text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-md active:scale-95 transition-all"
              >
                <Save size={18} /> {t.save}
              </button>
              <button
                onClick={() => {
                  if (window.confirm(t.confirmDel)) {
                    deleteDoc(
                      doc(db, "users", user!.uid, "pets", String(activePetId))
                    );
                    setEditing(false);
                  }
                }}
                className="w-full py-3 bg-[var(--red-soft)] text-[var(--red-text)] font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <Trash2 size={18} /> {t.delProfile}
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mb-4 px-1">
          <h3 className="font-extrabold text-xl text-[var(--text-main)]">
            {t.medCard}
          </h3>

          <div className="flex items-center gap-2">
            {!editing &&
              (activePet.gender === t.girl ||
                activePet.gender === "Дівчинка" ||
                activePet.gender === "Girl") &&
              !(activePet as any).isSterilized && (
                <div className="flex items-center gap-2 px-3 h-[36px] bg-[#a073e7] text-white rounded-xl shadow-sm">
                  <span className="text-[11px] font-black uppercase tracking-wider">
                    {lang === "uk" ? "Тічка" : "Heat"}
                  </span>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!user || !activePetId) return;
                      const today = new Date().toISOString().split("T")[0];
                      const petRef = doc(
                        db,
                        "users",
                        user.uid,
                        "pets",
                        String(activePetId)
                      );

                      if (!(activePet as any).isHeatActive) {
                        await updateDoc(petRef, {
                          isHeatActive: true,
                          heatStartDate: today,
                        });
                      } else {
                        const newRecord = {
                          start: (activePet as any).heatStartDate || today,
                          end: today,
                        };
                        await updateDoc(petRef, {
                          isHeatActive: false,
                          heatStartDate: null,
                          heatHistory: [
                            ...((activePet as any).heatHistory || []),
                            newRecord,
                          ],
                        });
                      }
                    }}
                    className={`w-9 h-5 rounded-full flex items-center p-0.5 transition-all shadow-inner ${
                      (activePet as any).isHeatActive
                        ? "bg-white"
                        : "bg-black/20"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full shadow-sm transition-transform ${
                        (activePet as any).isHeatActive
                          ? "translate-x-4 bg-[#a073e7]"
                          : "translate-x-0 bg-white"
                      }`}
                    />
                  </button>
                </div>
              )}
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-3 h-[36px] bg-[#a073e7] text-white rounded-xl text-sm font-bold shadow-sm active:scale-95 transition-all"
            >
              <Plus size={16} /> {t.add}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {reminders.filter((r) => r.petId === activePetId).length === 0 ? (
            <div className="text-center py-8 bg-[var(--bg-card)] rounded-2xl border-2 border-dashed border-[var(--border-color)] text-[var(--text-muted)] font-bold">
              {t.noReminders}
            </div>
          ) : (
            reminders
              .filter((r) => r.petId === activePetId)
              .map((r) => (
                <div
                  key={r.id}
                  className="bg-[var(--bg-card)] p-3 rounded-2xl flex gap-3 shadow-sm border border-[var(--border-color)] items-center relative overflow-hidden"
                >
                  <div
                    className={`w-11 h-11 rounded-xl ${
                      r.completed
                        ? "bg-[var(--bg-soft)] text-[var(--text-muted)]"
                        : "bg-[var(--accent-soft)] text-[#a073e7]"
                    } flex items-center justify-center shrink-0 transition-colors`}
                  >
                    {r.type === t.modPills ? (
                      <Pill size={22} />
                    ) : r.type === t.modVac ? (
                      <Syringe size={22} />
                    ) : (
                      <Stethoscope size={22} />
                    )}
                  </div>
                  <div
                    className={`flex-1 min-w-0 ${
                      r.completed ? "opacity-50" : ""
                    }`}
                  >
                    <p className="font-bold text-[15px] truncate text-[var(--text-main)]">
                      {r.title}
                    </p>
                    <p className="text-xs text-[var(--text-muted)]">
                      {formatDate(r.date)} {r.time ? `• ${r.time}` : ""}{" "}
                      {r.recurrence !== "none" && (
                        <Repeat size={10} className="inline ml-1" />
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 bg-[var(--bg-soft)] p-1 rounded-xl border border-[var(--border-color)]">
                    {r.notify === "true" && (
                      <BellRing
                        size={16}
                        className={`mx-1 transition-colors ${
                          r.completed
                            ? "text-[var(--text-muted)]"
                            : "text-[var(--red-text)]"
                        }`}
                      />
                    )}
                    <button
                      onClick={() => handleCompleteReminder(r)}
                      disabled={r.completed}
                      className={`p-2 rounded-lg shadow-sm border transition-all ${
                        r.completed
                          ? "bg-[var(--border-color)] text-[var(--text-muted)] opacity-60 cursor-not-allowed border-transparent"
                          : "bg-[var(--bg-card)] text-[#10b981] border-[var(--border-color)] active:scale-95"
                      }`}
                    >
                      <Check size={20} />
                    </button>
                    <button
                      onClick={() => handleDeleteReminder(r.id)}
                      className="p-2 text-[var(--text-muted)] hover:text-[var(--red-text)] transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {showSettings && (
        <ProfileSettings
          setShowSettings={setShowSettings}
          ownerName={ownerName}
          setOwnerName={setOwnerName}
          ownerPhone={ownerPhone}
          setOwnerPhone={setOwnerPhone}
        />
      )}
      {showQrModal && (
        <QrOrderModal
          setShowQrModal={setShowQrModal}
          activePetId={String(activePetId)}
          activePet={activePet}
          ownerName={ownerName}
          ownerPhone={ownerPhone}
        />
      )}
      {showInfo && (
        <MedicalInfoModal
          setShowInfo={setShowInfo}
          activePet={activePet}
          activePetId={String(activePetId)}
        />
      )}
      {showForm && (
        <ReminderForm
          setShowForm={setShowForm}
          activePetId={String(activePetId)}
        />
      )}
    </div>
  );
}
